import {Alert, Button, Input} from 'antd';
import {useEffect, useMemo, useState} from 'react';

import {Page} from '../../../layout/page/page';
import {Locale} from '../../../provider/locale/locale';
import {useLocale} from '../../../provider/locale/locale-hook';
import {useFormRules} from '../../../service/form/form-rules-hook';
import {Form} from '../../../typings/antd';
import {saveToLocalStorage} from '../../../util/local-storage';
import {useUrl} from '../../../util/url-hook/url-hook';
import {AuthFormFooter} from '../common/form-footer/auth-form-footer';
import {AuthFormHeader} from '../common/form-header/auth-form-header';

import {
    ssoClientCodeMaxLength,
    ssoClientCodePattern,
    ssoClientCodeStorageKey,
    ssoLoginClientCodeQueryName,
    SsoLoginErrorEnum,
    ssoLoginErrorQueryName,
    SsoLoginFormKeyEnum,
    SsoLoginFormType,
} from './sso-login-const';
import {
    getSsoLoginErrorLangKey,
    getSsoLoginInitialValues,
    getSsoLoginUrl,
    normalizeSsoClientCode,
} from './sso-login-helper';
import * as styles from './sso-login.scss';

export function SsoLogin(): JSX.Element {
    const [form] = Form.useForm<SsoLoginFormType>();
    const clientCode = Form.useWatch(SsoLoginFormKeyEnum.ClientCode, form);
    const {requiredFieldRule, maxLengthFieldRule} = useFormRules();
    const {getLocalizedString} = useLocale();
    const {getQuery} = useUrl();

    // The error of the attempt that has just failed; it is cleared once the code is edited.
    const [ssoError, setSsoError] = useState<string | null>(() => getQuery(ssoLoginErrorQueryName));
    const [isRedirecting, setIsRedirecting] = useState<boolean>(false);
    const initialValues = useMemo(
        () => getSsoLoginInitialValues(getQuery(ssoLoginClientCodeQueryName)),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        []
    );

    useEffect(() => {
        // Coming back from the identity provider with the browser "Back" button restores this page from bfcache.
        function onPageShow(event: {persisted: boolean}) {
            if (event.persisted) {
                setIsRedirecting(false);
            }
        }

        window.addEventListener('pageshow', onPageShow);

        return () => window.removeEventListener('pageshow', onPageShow);
    }, []);

    const isClientCodeNotFound = ssoError === SsoLoginErrorEnum.ConfigNotFound;
    const alertLangKey = ssoError && !isClientCodeNotFound ? getSsoLoginErrorLangKey(ssoError) : null;

    function onFinish(values: SsoLoginFormType) {
        const normalizedClientCode = normalizeSsoClientCode(values[SsoLoginFormKeyEnum.ClientCode]);

        saveToLocalStorage<string>(normalizedClientCode, ssoClientCodeStorageKey);
        setIsRedirecting(true);
        window.location.assign(getSsoLoginUrl(normalizedClientCode));
    }

    return (
        <Page.CenteredCard titleLangKey="PAGE__SSO_LOGIN__HEADER">
            <AuthFormHeader
                subtitle={<Locale stringKey="PAGE__SSO_LOGIN__SUB_HEADER" />}
                title={<Locale stringKey="PAGE__SSO_LOGIN__HEADER" />}
            />

            {alertLangKey && (
                <Alert
                    className={styles.SsoLogin_alert}
                    description={<Locale stringKey={alertLangKey} />}
                    message={<Locale stringKey="PAGE__SSO_LOGIN__ERROR__TITLE" />}
                    showIcon
                    type="error"
                />
            )}

            <Form<SsoLoginFormType>
                form={form}
                initialValues={initialValues}
                layout="vertical"
                onFinish={onFinish}
                onValuesChange={() => setSsoError(null)}
            >
                <Form.Item<SsoLoginFormType>
                    help={isClientCodeNotFound ? <Locale stringKey="PAGE__SSO_LOGIN__ERROR__CONFIG_NOT_FOUND" /> : null}
                    label={<Locale stringKey="PAGE__SSO_LOGIN__INPUT__CLIENT_CODE__LABEL" />}
                    name={SsoLoginFormKeyEnum.ClientCode}
                    rules={[
                        requiredFieldRule,
                        maxLengthFieldRule(ssoClientCodeMaxLength),
                        {
                            pattern: ssoClientCodePattern,
                            message: <Locale stringKey="PAGE__SSO_LOGIN__INPUT__CLIENT_CODE__INVALID" />,
                        },
                    ]}
                    validateStatus={isClientCodeNotFound ? 'error' : ''}
                >
                    <Input
                        autoComplete="organization"
                        placeholder={getLocalizedString('PAGE__SSO_LOGIN__INPUT__CLIENT_CODE__PLACEHOLDER')}
                        size="large"
                    />
                </Form.Item>

                <Button
                    block
                    disabled={!clientCode || isRedirecting}
                    htmlType="submit"
                    loading={isRedirecting}
                    size="large"
                    type="primary"
                >
                    <Locale stringKey="PAGE__SSO_LOGIN__BUTTON__CONTINUE" />
                </Button>
            </Form>

            <AuthFormFooter />
        </Page.CenteredCard>
    );
}
